import { apiInstance } from '@api/api'
import type {
  ApiQueryOptions,
  ApiReturnType,
  ApiSuspenseQueryOptions,
  ErrorResponse,
} from '@api/types'
import { setUser } from '@sentry/react'
import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

type Path = '/me'

type Data = ApiReturnType<Path, 'get', 200>

type Options<TSelected = Data> = ApiQueryOptions<Data, TSelected>

type SuspenseOptions<TSelected = Data> = ApiSuspenseQueryOptions<
  Data,
  TSelected
>

export function getAuthSelfQueryKey() {
  return ['auth', 'self', 'get'] as const
}

async function getAuthSelf() {
  const { data } = await apiInstance.get<Data>('/me')
  setUser({ id: data.user.id, email: data.user.email })
  return data
}

export function getAuthSelfQueryOptions<TSelected = Data>(
  options?: Options<TSelected>
) {
  return queryOptions<Data, AxiosError<ErrorResponse>, TSelected>({
    queryKey: getAuthSelfQueryKey(),
    queryFn: getAuthSelf,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  })
}

export function useGetAuthSelf<TSelected = Data>(options?: Options<TSelected>) {
  return useQuery(getAuthSelfQueryOptions(options))
}

export function useGetAuthSelfSuspense<TSelected = Data>(
  options?: SuspenseOptions<TSelected>
) {
  return useSuspenseQuery<Data, AxiosError<ErrorResponse>, TSelected>({
    ...getAuthSelfQueryOptions(),
    ...options,
  })
}
